import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  BlockOutlined,
  ChatOutlined,
  Download2FilledOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  MoreVertOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { useCancelPayFile } from "../hooks/useCancelPayFile"

import { PayFileCancelConfirmationDialog } from "./PayFileCancelConfirmationDialog"

import { CommentsListDialog } from "@/components/Comment/CommentsListDialog"
import { CommentParentTableType, type PayFileListFieldsFragment, PayFileStatus } from "@/graphql/codegen/graphql"

interface PayFileRowActionsProps {
  row: CellContext<PayFileListFieldsFragment, unknown>["row"]
  onExport: (payFile: Pick<PayFileListFieldsFragment, "payFileId" | "referenceNumber">) => Promise<boolean>
  onSuccess: () => void
}

export const PayFileRowActions: FC<PayFileRowActionsProps> = ({ row, onExport, onSuccess }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { cancelPayFile, isCancelling } = useCancelPayFile()

  const payFile = row.original
  // AK-7179: Cancel Pay File is offered on Pending and Approved pay files; Cancelled and Sent are terminal and get
  // Comments and Export only. Processing is left out too: the backend rejects cancelling a file that is being sent.
  const canCancel = payFile.status === PayFileStatus.Pending || payFile.status === PayFileStatus.Approved

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleComments = () => {
    handleMenuClose()
    setIsCommentsOpen(true)
  }

  const handleExport = () => {
    handleMenuClose()
    void onExport(payFile)
  }

  const handleCancel = () => {
    handleMenuClose()
    setIsCancelConfirmationOpen(true)
  }

  // The confirmation cannot be dismissed while the request is running, so a click on Go Back or the backdrop cannot hide
  // an outcome the user is still waiting for.
  const handleCancelClose = () => {
    if (isCancelling) return
    setIsCancelConfirmationOpen(false)
  }

  // A failed mutation leaves the confirmation open: the hook already notified the error, so the user can retry or go
  // back without re-opening it from the row.
  const handleCancelConfirm = async (comment: string) => {
    const isSuccess = await cancelPayFile({ payFileId: payFile.payFileId, comment })
    if (!isSuccess) return

    setIsCancelConfirmationOpen(false)
    onSuccess()
  }

  return (
    <Box className='flex items-center justify-end gap-x-3' ref={anchorElRef}>
      <IconButton
        size='small'
        onClick={handleMenuOpen}
        data-testid={`pay-file.row-actions.menu-button-${payFile.payFileId}`}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid={`pay-file.row-actions.menu-${payFile.payFileId}`}
      >
        <MenuItem onClick={handleComments} data-testid={`pay-file.row-actions.comments-button-${payFile.payFileId}`}>
          <ListItemIcon>
            <ChatOutlined />
          </ListItemIcon>
          <ListItemText primary='Comments' />
        </MenuItem>
        <MenuItem onClick={handleExport} data-testid={`pay-file.row-actions.export-button-${payFile.payFileId}`}>
          <ListItemIcon>
            <Download2FilledOutlined />
          </ListItemIcon>
          <ListItemText primary='Export to Excel' />
        </MenuItem>
        {canCancel && (
          <MenuItem onClick={handleCancel} data-testid={`pay-file.row-actions.cancel-button-${payFile.payFileId}`}>
            <ListItemIcon>
              <BlockOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel Pay File' />
          </MenuItem>
        )}
      </Menu>
      {isCancelConfirmationOpen && (
        <PayFileCancelConfirmationDialog
          isSubmitting={isCancelling}
          onClose={handleCancelClose}
          onConfirm={handleCancelConfirm}
        />
      )}
      {isCommentsOpen && (
        <CommentsListDialog
          title='Comments'
          commentParentTableType={CommentParentTableType.PayFile}
          referenceId={payFile.payFileId}
          onClose={() => setIsCommentsOpen(false)}
        />
      )}
      <IconButton
        size='small'
        onClick={row.getToggleExpandedHandler()}
        data-testid={`pay-file.row-actions.expand-button-${payFile.payFileId}`}
      >
        {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
      </IconButton>
    </Box>
  )
}
